/**
 * Maps TinyMCE commands and format names to their corresponding CSS properties.
 * Used to detect which CSS properties are being modified when a user makes styling changes.
 */

/**
 * Theme-controlled CSS properties that may need preservation markers.
 * Only these properties are managed by the theme application logic.
 */
export const THEME_CONTROLLED_PROPERTIES = [
  'font-family',
  'color',
  'font-weight',
  'font-style',
  'text-transform',
] as const

export type ThemeControlledProperty =
  (typeof THEME_CONTROLLED_PROPERTIES)[number]

/**
 * Maps TinyMCE command names (lowercase) to CSS property names.
 * These commands are triggered via toolbar buttons or keyboard shortcuts.
 */
const COMMAND_TO_CSS_MAP: Record<string, ThemeControlledProperty | null> = {
  // Font commands
  fontname: 'font-family',
  mcefontname: 'font-family',

  // Color commands
  forecolor: 'color',
  mceforecolor: 'color',

  // Bold/Italic commands
  bold: 'font-weight',
  mcetoggleformat_bold: 'font-weight',
  italic: 'font-style',
  mcetoggleformat_italic: 'font-style',

  // Text transform is typically not a direct command but handled via custom formats
}

/**
 * Maps TinyMCE format names to CSS property names.
 * Formats are applied via editor.formatter.apply() or format-related events.
 */
const FORMAT_TO_CSS_MAP: Record<string, ThemeControlledProperty | null> = {
  bold: 'font-weight',
  italic: 'font-style',
  fontname: 'font-family',
  forecolor: 'color',

  // Text transform format registered by registerTextCasingPlugin.js
  customtexttransform: 'text-transform',
}

/**
 * Determines if a TinyMCE command affects a theme-controlled CSS property.
 * @param command - The TinyMCE command name (case-insensitive)
 * @returns The CSS property name if it's theme-controlled, null otherwise
 */
export const getCSSPropertyFromCommand = (
  command: string,
): ThemeControlledProperty | null => {
  const normalizedCommand = command.toLowerCase()
  return COMMAND_TO_CSS_MAP[normalizedCommand] ?? null
}

/**
 * Determines if a TinyMCE format affects a theme-controlled CSS property.
 * @param format - The TinyMCE format name
 * @returns The CSS property name if it's theme-controlled, null otherwise
 */
export const getCSSPropertyFromFormat = (
  format: string,
): ThemeControlledProperty | null => {
  const normalizedFormat = format.toLowerCase()
  return FORMAT_TO_CSS_MAP[normalizedFormat] ?? null
}

/**
 * Checks if a CSS property is controlled by the theme application logic.
 * @param property - The CSS property name
 * @returns true if the property is theme-controlled
 */
export const isThemeControlledProperty = (
  property: string,
): property is ThemeControlledProperty => {
  return THEME_CONTROLLED_PROPERTIES.includes(
    property as ThemeControlledProperty,
  )
}
